import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { Plus, Trash, MagnifyingGlass, Printer, PencilSimple, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const TABS = [
  { key: "materia_prima", label: "Materie Prime" },
  { key: "semilavorato", label: "Semilavorati" },
  { key: "prodotto_finito", label: "Prodotti Finiti" },
  { key: "imballaggio", label: "Imballaggi" },
];

export default function Prodotti() {
  const [businessUnit, setBusinessUnit] = useState("chimica"); // "chimica" | "axelplast"
  const [active, setActive] = useState("materia_prima");
  const [items, setItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productIdsWithBOM, setProductIdsWithBOM] = useState(new Set());
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create, string = edit
  const [form, setForm] = useState(empty());
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importReport, setImportReport] = useState(null);

  function empty() {
    return {
      name: "",
      type: "materia_prima",
      unit: "L",
      category: "",
      supplier: "",
      description: "",
      min_stock: 0,
      initial_stock: 0,
      purchase_cost: 0,
      barcode: "",
      business_unit: "chimica",
    };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, all, formulas] = await Promise.all([
        api.get("/products", { params: { type: active, search, business_unit: businessUnit } }),
        api.get("/products", { params: { business_unit: businessUnit } }),
        api.get("/formulas"),
      ]);
      setItems(data);
      setAllProducts(all.data);
      setProductIdsWithBOM(new Set(formulas.data.map((f) => f.output_product_id)));
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [active, search, businessUnit]);

  // Categories and suppliers filtered by current form's product type for relevance
  const allCategories = Array.from(
    new Set(
      allProducts
        .filter((p) => p.type === form.type)
        .map((p) => (p.category || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "it"));

  const allSuppliers = Array.from(
    new Set(
      allProducts
        .filter((p) => p.type === form.type)
        .map((p) => (p.supplier || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "it"));

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...empty(), type: active, business_unit: businessUnit });
    setEditingId(null);
    setCreating(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      type: p.type,
      unit: p.unit,
      category: p.category || "",
      supplier: p.supplier || "",
      description: p.description || "",
      min_stock: p.min_stock,
      initial_stock: p.stock,
      purchase_cost: p.purchase_cost ?? 0,
      barcode: p.barcode,
    });
    setEditingId(p.id);
    setCreating(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Resolve unconfirmed "+ Nuova categoria" state: take the draft if present
    const resolvedCategory =
      form.category === "__new__" ? (form.categoryDraft || "").trim() : (form.category || "");
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, {
          name: form.name,
          unit: form.unit,
          category: resolvedCategory || null,
          supplier: form.supplier || null,
          description: form.description || null,
          min_stock: Number(form.min_stock),
          purchase_cost: Number(form.purchase_cost),
        });
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/products", {
          ...form,
          category: resolvedCategory || null,
          min_stock: Number(form.min_stock),
          initial_stock: Number(form.initial_stock),
          purchase_cost: Number(form.purchase_cost),
          barcode: form.barcode || null,
        });
        toast.success("Prodotto creato");
      }
      setCreating(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il prodotto?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Prodotto eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateCategory = async (p, newCategory) => {
    if (newCategory === (p.category || "")) return;
    try {
      await api.put(`/products/${p.id}`, { category: newCategory || null });
      toast.success(
        newCategory
          ? `Categoria di "${p.name}" → ${newCategory}`
          : `Categoria rimossa da "${p.name}"`
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Categories used by ALL products of the row's type (computed lazily per row)
  const categoriesByType = (type) =>
    Array.from(
      new Set(
        allProducts
          .filter((x) => x.type === type)
          .map((x) => (x.category || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "it"));

  const submitImport = async (e) => {
    e.preventDefault();
    if (!importFile) return toast.error("Seleziona un file CSV");
    setImportBusy(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/movements/inventory/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(data);
      toast.success(`Inventario aggiornato: ${data.updated} prodotti`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="prodotti-page">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Anagrafica · EAN-13 ·{" "}
            <span style={{ color: businessUnit === "axelplast" ? "#F4A261" : "#1D3557" }}>
              {businessUnit === "axelplast" ? "Axelplast" : "Chimica Fotografica"}
            </span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Prodotti
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const { data } = await api.get(
                  `/movements/inventory/template.csv`,
                  { params: { business_unit: businessUnit }, responseType: "blob" }
                );
                const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `inventario-template-${businessUnit}-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Template CSV scaricato");
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            data-testid="export-inventory-btn"
            title="Scarica template CSV con tutti i prodotti correnti"
            className="bg-white hover:bg-[#F3F4F6] text-[#0A0A0A] border border-[#E5E7EB] px-4 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
          >
            <DownloadSimple size={14} weight="bold" /> Esporta CSV
          </button>
          <button
            onClick={() => setImportOpen(true)}
            data-testid="import-inventory-btn"
            title="Importa inventario da CSV"
            className="bg-[#2A9D8F] hover:bg-[#218B7E] text-white px-4 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
          >
            <UploadSimple size={14} weight="bold" /> Importa Inventario
          </button>
          <button
            onClick={openCreate}
            data-testid="new-product-btn"
            className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
          >
            <Plus size={14} weight="bold" /> Nuovo Prodotto
          </button>
        </div>
      </div>

      <div className="mb-5 inline-flex bg-[#F3F4F6] p-1 border border-[#E5E7EB]" data-testid="business-unit-switcher">
        {[
          { key: "chimica", label: "Chimica Fotografica", color: "#1D3557" },
          { key: "axelplast", label: "Axelplast", color: "#F4A261" },
        ].map((b) => (
          <button
            key={b.key}
            onClick={() => {
              if (businessUnit === b.key) return;
              setBusinessUnit(b.key);
              setCategoryFilter("");
              setSearch("");
            }}
            data-testid={`bu-${b.key}`}
            className={`px-4 py-2 text-xs font-display font-semibold uppercase tracking-wider transition-colors ${
              businessUnit === b.key
                ? "bg-white text-[#0A0A0A] shadow-sm"
                : "text-[#4B5563] hover:text-[#0A0A0A]"
            }`}
            style={businessUnit === b.key ? { borderBottom: `2px solid ${b.color}` } : {}}
          >
            {b.label}
          </button>
        ))}
      </div>

      <div className="border-b border-[#E5E7EB] mb-6 flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setActive(t.key);
              setCategoryFilter(""); // reset filter when switching tab
            }}
            data-testid={`tab-${t.key}`}
            className={`px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
              active === t.key
                ? "border-[#1D3557] text-[#0A0A0A]"
                : "border-transparent text-[#4B5563] hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center mb-6 gap-3">
        <div className="relative w-full max-w-md">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]"
          />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome o codice a barre…"
            className="w-full pl-9 pr-4 py-2.5 border border-[#E5E7EB] bg-white text-sm focus:ring-2 focus:ring-[#1D3557] focus:border-transparent outline-none"
          />
        </div>

        {/* Category filter — only categories of products in current tab */}
        {(() => {
          const cats = Array.from(
            new Set(items.map((p) => (p.category || "").trim()).filter(Boolean))
          ).sort((a, b) => a.localeCompare(b, "it"));
          if (cats.length === 0) return null;
          return (
            <select
              data-testid="category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#1D3557] focus:border-transparent outline-none min-w-[200px]"
            >
              <option value="">Tutte le categorie ({cats.length})</option>
              {cats.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          );
        })()}

        {categoryFilter && (
          <button
            onClick={() => setCategoryFilter("")}
            data-testid="clear-category-filter"
            className="text-xs text-[#4B5563] hover:text-[#E63946] underline"
          >
            Rimuovi filtro
          </button>
        )}

        <div className="ml-auto text-xs text-[#4B5563] font-mono-data flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#2A9D8F]" /> = ha distinta base
          </span>
          <span>·</span>
          {(() => {
            const filtered = categoryFilter
              ? items.filter((p) => p.category === categoryFilter)
              : items;
            return `${filtered.length} prodott${filtered.length === 1 ? "o" : "i"}`;
          })()}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] overflow-x-auto" data-testid="products-table">
        <table className="w-full">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
              <th className="px-6 py-3 font-semibold">EAN-13</th>
              <th className="px-6 py-3 font-semibold">Nome</th>
              <th className="px-6 py-3 font-semibold">Categoria</th>
              <th className="px-6 py-3 font-semibold">Fornitore</th>
              <th className="px-6 py-3 font-semibold text-right">Stock</th>
              <th className="px-6 py-3 font-semibold text-right">Min</th>
              <th className="px-6 py-3 font-semibold text-right">Costo €/u.</th>
              <th className="px-6 py-3 font-semibold text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const filteredItems = categoryFilter
                ? items.filter((p) => p.category === categoryFilter)
                : items;
              if (loading) {
                return (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-sm text-[#4B5563]">
                      Caricamento…
                    </td>
                  </tr>
                );
              }
              if (filteredItems.length === 0) {
                return (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-sm text-[#4B5563]">
                      {categoryFilter
                        ? `Nessun prodotto nella categoria "${categoryFilter}".`
                        : "Nessun prodotto. Aggiungi il primo con "}
                      {!categoryFilter && <b>Nuovo Prodotto</b>}
                      {!categoryFilter && "."}
                    </td>
                  </tr>
                );
              }
              return filteredItems.map((p) => {
                const low = p.min_stock > 0 && p.stock <= p.min_stock;
                return (
                  <tr
                    key={p.id}
                    className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]"
                    data-testid={`product-row-${p.id}`}
                  >
                    <td className="px-6 py-4 font-mono-data text-xs text-[#0A0A0A]">
                      {p.barcode}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#0A0A0A] font-medium">
                      <div className="flex items-center gap-2">
                        {productIdsWithBOM.has(p.id) && (
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full bg-[#2A9D8F] flex-shrink-0"
                            title="Distinta base presente"
                            data-testid={`bom-dot-${p.id}`}
                          />
                        )}
                        <span>{p.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4B5563]">
                      {(() => {
                        const cats = categoriesByType(p.type);
                        return (
                          <select
                            data-testid={`inline-category-${p.id}`}
                            value={p.category || ""}
                            onChange={async (e) => {
                              const v = e.target.value;
                              if (v === "__new__") {
                                const name = window.prompt(
                                  "Nome nuova categoria:",
                                  p.category || ""
                                );
                                if (name === null) return;
                                await updateCategory(p, name.trim());
                              } else {
                                await updateCategory(p, v);
                              }
                            }}
                            className="bg-transparent border border-transparent hover:border-[#E5E7EB] focus:border-[#1D3557] focus:bg-white px-1.5 py-1 text-sm cursor-pointer outline-none transition-colors min-w-[140px]"
                            title="Clicca per cambiare categoria"
                          >
                            <option value="">—</option>
                            {/* Ensure current value is selectable even if not in list yet */}
                            {p.category && !cats.includes(p.category) && (
                              <option value={p.category}>{p.category}</option>
                            )}
                            {cats.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                            <option value="__new__">+ Nuova categoria…</option>
                          </select>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#4B5563]">{p.supplier || "—"}</td>
                    <td
                      className={`px-6 py-4 text-right font-mono-data text-sm font-semibold ${
                        low ? "text-[#E63946]" : "text-[#0A0A0A]"
                      }`}
                    >
                      {p.stock} {p.unit}
                    </td>
                    <td className="px-6 py-4 text-right font-mono-data text-xs text-[#4B5563]">
                      {p.min_stock} {p.unit}
                    </td>
                    <td className="px-6 py-4 text-right font-mono-data text-xs text-[#0A0A0A]">
                      {p.purchase_cost > 0 ? `€ ${Number(p.purchase_cost).toFixed(4)}` : "—"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`${api.defaults.baseURL}/labels/barcode/${p.barcode}.png`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`print-barcode-${p.id}`}
                          className="text-[#4B5563] hover:text-[#1D3557] p-1"
                          title="Visualizza codice a barre"
                        >
                          <Printer size={16} weight="bold" />
                        </a>
                        <button
                          onClick={() => openEdit(p)}
                          data-testid={`edit-product-${p.id}`}
                          className="text-[#4B5563] hover:text-[#1D3557] p-1"
                          title="Modifica"
                        >
                          <PencilSimple size={16} weight="bold" />
                        </button>
                        <button
                          onClick={() => remove(p.id)}
                          data-testid={`delete-product-${p.id}`}
                          className="text-[#4B5563] hover:text-[#E63946] p-1"
                          title="Elimina"
                        >
                          <Trash size={16} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-xl" data-testid="new-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editingId ? "Modifica Prodotto" : "Nuovo Prodotto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Nome *
              </label>
              <input
                required
                data-testid="product-name-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Tipologia
                </label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v })}
                  disabled={!!editingId}
                >
                  <SelectTrigger
                    data-testid="product-type-select"
                    className="rounded-none disabled:opacity-50"
                    disabled={!!editingId}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="materia_prima">Materia Prima</SelectItem>
                    <SelectItem value="semilavorato">Semilavorato</SelectItem>
                    <SelectItem value="prodotto_finito">Prodotto Finito</SelectItem>
                    <SelectItem value="imballaggio">Imballaggio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Unità di misura
                </label>
                <Select
                  value={form.unit}
                  onValueChange={(v) => setForm({ ...form, unit: v })}
                >
                  <SelectTrigger data-testid="product-unit-select" className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Litri (L)</SelectItem>
                    <SelectItem value="ml">Millilitri (ml)</SelectItem>
                    <SelectItem value="kg">Chilogrammi (kg)</SelectItem>
                    <SelectItem value="g">Grammi (g)</SelectItem>
                    <SelectItem value="m">Metri (m)</SelectItem>
                    <SelectItem value="pz">Pezzi (pz)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Categoria
                </label>
                {(() => {
                  // When the current value is empty or matches a known one, show the dropdown.
                  // When user picks "+ Nuova categoria", show a text input.
                  const isNew = form.category === "__new__";
                  const showInput = isNew;
                  return showInput ? (
                    <div className="flex gap-2">
                      <input
                        autoFocus
                        data-testid="product-category-new-input"
                        className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                        value={form.categoryDraft || ""}
                        onChange={(e) => setForm({ ...form, categoryDraft: e.target.value })}
                        onBlur={() => {
                          const draft = (form.categoryDraft || "").trim();
                          setForm({ ...form, category: draft, categoryDraft: undefined });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const draft = (form.categoryDraft || "").trim();
                            setForm({ ...form, category: draft, categoryDraft: undefined });
                          }
                          if (e.key === "Escape") {
                            setForm({ ...form, category: "", categoryDraft: undefined });
                          }
                        }}
                        placeholder="Nome nuova categoria…"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, category: "", categoryDraft: undefined })}
                        className="px-3 py-2 border border-[#E5E7EB] text-xs text-[#4B5563] hover:text-[#E63946]"
                        title="Annulla"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <Select
                      value={form.category || "__none__"}
                      onValueChange={(v) => {
                        if (v === "__new__") {
                          setForm({ ...form, category: "__new__", categoryDraft: "" });
                        } else if (v === "__none__") {
                          setForm({ ...form, category: "" });
                        } else {
                          setForm({ ...form, category: v });
                        }
                      }}
                    >
                      <SelectTrigger
                        data-testid="product-category-select"
                        className="rounded-none"
                      >
                        <SelectValue placeholder="Seleziona categoria…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Nessuna —</SelectItem>
                        {allCategories.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                        <SelectItem value="__new__">+ Nuova categoria…</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
                {allCategories.length > 0 && (
                  <div className="text-[10px] text-[#4B5563] mt-1">
                    {allCategories.length} categori{allCategories.length === 1 ? "a" : "e"} per{" "}
                    {form.type.replace("_", " ")}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Fornitore
                </label>
                <input
                  list="product-suppliers-list"
                  data-testid="product-supplier-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                  placeholder="Scegli o digita…"
                />
                <datalist id="product-suppliers-list">
                  {allSuppliers.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  {editingId ? "Stock attuale" : "Stock iniziale"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  data-testid="product-initial-stock-input"
                  disabled={!!editingId}
                  title={editingId ? "Lo stock si modifica solo tramite movimenti (Scanner)" : ""}
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
                  value={form.initial_stock}
                  onChange={(e) => setForm({ ...form, initial_stock: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Soglia minima
                </label>
                <input
                  type="number"
                  step="0.01"
                  data-testid="product-min-stock-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                  value={form.min_stock}
                  onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                  Costo d'acquisto € / {form.unit}
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  data-testid="product-cost-input"
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2A9D8F] font-mono-data"
                  value={form.purchase_cost}
                  onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                EAN-13 {editingId ? "" : "(opz.)"}
              </label>
              <input
                data-testid="product-barcode-input"
                placeholder="Auto"
                disabled={!!editingId}
                title={editingId ? "Il codice a barre non è modificabile dopo la creazione" : ""}
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
                value={form.barcode}
                onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              />
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
              >
                Annulla
              </button>
              <button
                type="submit"
                data-testid="submit-product-btn"
                className="bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider"
              >
                {editingId ? "Salva Modifiche" : "Crea Prodotto"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* IMPORT INVENTARIO CSV DIALOG */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportFile(null); setImportReport(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="import-inventory-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <UploadSimple size={20} weight="bold" /> Importa Inventario da CSV
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitImport} className="space-y-4">
            <div className="bg-[#EEF2F7] border border-[#1D3557]/20 p-3 text-xs text-[#0A0A0A]">
              <div className="font-semibold mb-1">Come funziona</div>
              <ol className="list-decimal pl-5 space-y-1 text-[#4B5563]">
                <li>Scarica il template con il pulsante <b>Esporta CSV</b> (in alto a destra).</li>
                <li>Apri il file con Excel o LibreOffice e compila la colonna <b>quantita_inventario</b> per i prodotti che vuoi caricare.</li>
                <li>Lascia vuote le righe che vuoi saltare.</li>
                <li>Salva come CSV (separatore <code>;</code>) e caricalo qui.</li>
              </ol>
              <div className="mt-2 text-[#4B5563]">
                ⚠ L'import <b>SOSTITUISCE</b> lo stock con il valore indicato (semantica inventario fisico). Crea un movimento di tipo <i>inventario</i> per ogni riga aggiornata.
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                File CSV (separatore <code>;</code> o <code>,</code>)
              </label>
              <input
                type="file"
                accept=".csv,text/csv"
                data-testid="import-file-input"
                onChange={(e) => { setImportFile(e.target.files[0] || null); setImportReport(null); }}
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm bg-white file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#1D3557] file:text-white file:text-xs file:uppercase file:font-display file:font-semibold"
              />
              {importFile && (
                <div className="text-xs text-[#4B5563] mt-1 font-mono-data">
                  📄 {importFile.name} · {(importFile.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>

            {importReport && (
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3 space-y-2 text-xs">
                <div className="grid grid-cols-4 gap-2 font-mono-data">
                  <div>
                    <div className="text-[10px] uppercase text-[#4B5563]">Righe</div>
                    <div className="text-base font-bold">{importReport.total_rows}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#2A9D8F]">Aggiornati</div>
                    <div className="text-base font-bold text-[#2A9D8F]">{importReport.updated}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#4B5563]">Saltate (vuote)</div>
                    <div className="text-base font-bold">{importReport.skipped_empty}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#E63946]">Non trovati</div>
                    <div className="text-base font-bold text-[#E63946]">{importReport.skipped_not_found.length}</div>
                  </div>
                </div>
                {importReport.skipped_not_found.length > 0 && (
                  <div className="bg-[#FBEDEE] border border-[#E63946]/30 p-2 text-[#E63946]">
                    <div className="font-semibold mb-1">Codici a barre non trovati:</div>
                    <div className="font-mono-data">
                      {importReport.skipped_not_found.slice(0, 10).join(", ")}
                      {importReport.skipped_not_found.length > 10 && ` … e altri ${importReport.skipped_not_found.length - 10}`}
                    </div>
                  </div>
                )}
                {importReport.applied.length > 0 && (
                  <div className="max-h-48 overflow-y-auto bg-white border border-[#E5E7EB]">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F3F4F6] sticky top-0">
                        <tr className="text-left">
                          <th className="px-2 py-1 font-semibold">Prodotto</th>
                          <th className="px-2 py-1 font-semibold text-right">Prima</th>
                          <th className="px-2 py-1 font-semibold text-right">Dopo</th>
                          <th className="px-2 py-1 font-semibold text-right">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importReport.applied.map((a, i) => (
                          <tr key={i} className="border-t border-[#E5E7EB]">
                            <td className="px-2 py-1">{a.name}</td>
                            <td className="px-2 py-1 text-right font-mono-data">{a.old_stock}</td>
                            <td className="px-2 py-1 text-right font-mono-data font-semibold">{a.new_stock}</td>
                            <td className={`px-2 py-1 text-right font-mono-data ${a.delta > 0 ? "text-[#2A9D8F]" : a.delta < 0 ? "text-[#E63946]" : "text-[#4B5563]"}`}>
                              {a.delta > 0 ? "+" : ""}{a.delta}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => setImportOpen(false)}
                className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
              >
                Chiudi
              </button>
              <button
                type="submit"
                disabled={!importFile || importBusy}
                data-testid="submit-import-btn"
                className="bg-[#2A9D8F] hover:bg-[#218B7E] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider"
              >
                {importBusy ? "Caricamento…" : "Importa CSV"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
